import { useQuery } from '@tanstack/react-query';

import { mapPpaViewMetadataToScenarioViewData } from 'components/molecules/PricingSimulatorV3/ScenarioLensSection/scenarioLens.helpers';
import type { ScenarioViewData } from 'components/molecules/PricingSimulatorV3/ScenarioLensSection/scenarioLens.types';
import { fetchPpaViewMetadata } from 'hooks/ppa/usePpaViews';
import type { PPAViewDataSource, PPAViewListItem } from 'types/ppaViews';
import { getAuthUserMail } from 'utils/helperMethods';

type UseFetchViewMetadataParams = {
    viewId?: number | null;
    scenarioId?: number | null;
    dataSource?: PPAViewDataSource;
    enabled?: boolean;
};

export const getPpaViewMetadataQueryKey = (
    viewId: number,
    scenarioId?: number | null,
    dataSource: PPAViewDataSource = 'view',
    userId?: string | null,
) =>
    [
        'ppa-view-metadata',
        viewId,
        scenarioId ?? null,
        dataSource,
        (userId ?? getAuthUserMail()) || null,
    ] as const;

export const resolveDefaultViewId = (views: PPAViewListItem[]): number | null =>
    views.find((view) => view.is_default)?.view_id ?? views[0]?.view_id ?? null;

const VIEW_METADATA_STALE_TIME_MS = 1000 * 60 * 60;
const SCENARIO_VIEW_METADATA_STALE_TIME_MS = 1000 * 30;

export async function fetchScenarioViewMetadata(
    viewId: number,
    options?: {
        scenarioId?: number | null;
        dataSource?: PPAViewDataSource;
    },
): Promise<ScenarioViewData> {
    const metadata = await fetchPpaViewMetadata(viewId, options);
    return mapPpaViewMetadataToScenarioViewData(metadata);
}

export function useFetchViewMetadata({
    viewId,
    scenarioId = null,
    dataSource = 'view',
    enabled = true,
}: UseFetchViewMetadataParams = {}) {
    return useQuery({
        queryKey:
            viewId != null
                ? getPpaViewMetadataQueryKey(viewId, scenarioId, dataSource)
                : ['ppa-view-metadata', viewId, scenarioId, dataSource, null],
        queryFn: async (): Promise<ScenarioViewData> => {
            if (viewId == null) {
                throw new Error('viewId is required to fetch view metadata');
            }
            return fetchScenarioViewMetadata(viewId, { scenarioId, dataSource });
        },
        enabled: enabled && viewId != null,
        staleTime: scenarioId != null ? SCENARIO_VIEW_METADATA_STALE_TIME_MS : VIEW_METADATA_STALE_TIME_MS,
        gcTime: scenarioId != null ? 0 : undefined,
        refetchOnMount: scenarioId != null ? false : true,
    });
}

export default useFetchViewMetadata;
